import React, { useState, useEffect, useCallback, useRef } from 'react';
import { css } from '@emotion/react';

// ─── Types ───────────────────────────────────────────────────────────────────

type ReqStatus = 'done' | 'pending' | 'not-required';

type Requirement = {
  id: string;
  title: string;
  desc: string;
  status: ReqStatus;
  cta: string | null;
};

type LimitItem =
  | { type: 'segment'; label: string; resolveType: 'wait' | 'domain' }
  | {
      type: 'stat';
      label: string;
      value: string;
      valClass: string;
      itemClass: string;
      resolveType: 'wait' | 'domain';
    };

type RestrictionStatus = 'active' | 'resolved';

type Restriction = {
  id: string;
  title: string;
  desc: string;
  severity: 'high' | 'medium' | 'resolved';
  since: string;
  requirements: string[];
  status: RestrictionStatus;
  limitItems: LimitItem[];
};

type HubTab = 'setup' | 'restrictions';

// ─── Data ────────────────────────────────────────────────────────────────────

const REQUIREMENTS: Requirement[] = [
  {
    id: 'domain',
    title: 'Custom domain verification',
    desc: "Connect your own sending domain to improve inbox placement and build trust with your subscribers' email providers.",
    status: 'pending',
    cta: 'Set up now',
  },
  {
    id: 'postmaster',
    title: 'Google Postmaster integration',
    desc: 'Link Google Postmaster Tools so we can monitor your domain reputation and alert you before issues affect your Gmail delivery.',
    status: 'pending',
    cta: 'Connect now',
  },
  {
    id: 'optin',
    title: 'Enable double opt-in for forms',
    desc: 'Ask new subscribers to confirm their email before joining your list. This keeps your list healthy and your open rates high.',
    status: 'pending',
    cta: 'Go to forms',
  },
  {
    id: 'segments',
    title: 'Create smart segments',
    desc: 'Organise your audience by engagement so you can send to the right people at the right time — and protect your sending reputation.',
    status: 'done',
    cta: 'View segments',
  },
  {
    id: 'unengaged',
    title: 'Remove or archive unengaged subscribers',
    desc: "Sending to inactive contacts hurts your deliverability. We'll help you identify and clean them up in just a few steps.",
    status: 'pending',
    cta: 'Clean my list',
  },
  {
    id: 'kyc',
    title: 'Identity verification (KYC)',
    desc: 'Verify your identity to unlock higher sending volumes and keep your account in good standing with email providers.',
    status: 'not-required',
    cta: null,
  },
];

const RESTRICTIONS: Restriction[] = [
  {
    id: 'r1',
    title: 'High bounces of recipient quality',
    desc: 'A high number of your recent emails bounced because they were sent to inactive or invalid addresses. Cleaning your list will help bring your bounce rate back to a healthy level.',
    severity: 'medium',
    since: 'Mar 15, 2026',
    requirements: ['Remove or archive unengaged subscribers', 'Enable double opt-in for forms'],
    status: 'active',
    limitItems: [
      { type: 'segment', label: 'Only send to engaged smart segment', resolveType: 'wait' },
    ],
  },
  {
    id: 'r2',
    title: 'Repeated high bounces of low sender reputation',
    desc: "Your sending domain has a low reputation with major email providers, causing a high bounce rate. You'll need to verify your domain, complete KYC, and connect Google Postmaster before sending is permitted.",
    severity: 'high',
    since: 'Mar 13, 2026',
    requirements: [
      'Google Postmaster integration',
      'Custom domain verification',
      'Complete KYC process',
    ],
    status: 'active',
    limitItems: [
      { type: 'segment', label: 'Only send to engaged smart segment', resolveType: 'wait' },
    ],
  },
  {
    id: 'r3',
    title: 'Medium bounce of authentication',
    desc: 'Your account is sending from a shared domain. To unlock full sending capabilities, you must authenticate your own custom domain with SPF, DKIM, and DMARC.',
    severity: 'medium',
    since: 'Mar 10, 2026',
    requirements: ['Custom domain verification'],
    status: 'active',
    limitItems: [
      {
        type: 'stat',
        label: 'Daily send limit',
        value: '5,000',
        valClass: 'amber',
        itemClass: 'warn',
        resolveType: 'domain',
      },
    ],
  },
  {
    id: 'r4',
    title: 'In-progress campaign cancelled',
    desc: 'An active campaign was halted on Mar 8 due to a spam rate spike. Your next send will require completing the steps listed below.',
    severity: 'high',
    since: 'Mar 8, 2026',
    requirements: [
      'Remove or archive unengaged subscribers',
      'Enable double opt-in for forms',
      'Custom domain verification',
    ],
    status: 'active',
    limitItems: [
      {
        type: 'stat',
        label: 'Daily send limit',
        value: '0',
        valClass: 'red',
        itemClass: 'blocked',
        resolveType: 'wait',
      },
    ],
  },
  {
    id: 'r5',
    title: 'KYC verification completed — restriction lifted',
    desc: 'Your identity verification was completed successfully. Sending limits related to this requirement have been removed.',
    severity: 'resolved',
    since: 'Feb 28, 2026',
    requirements: [],
    status: 'resolved',
    limitItems: [],
  },
];

// ─── Countdown helpers ────────────────────────────────────────────────────────

const THIRTY_DAYS_SECS = 30 * 24 * 3600;

function getSecondsLeft(key: string): number {
  const storageKey = `restrict_start_${key}`;
  let start = localStorage.getItem(storageKey);
  if (!start) {
    start = String(Date.now());
    localStorage.setItem(storageKey, start);
  }
  const elapsed = Math.floor((Date.now() - Number(start)) / 1000);
  return Math.max(0, THIRTY_DAYS_SECS - elapsed);
}

function formatCountdown(secs: number): string {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(d).padStart(2, '0')}d ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const appStyles = css`
  max-width: 860px;
  margin: 0 auto;
  padding: 0 0 48px;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
  background: var(--grn-color-background, #faf9f7);
  color: var(--grn-color-content, #1a1a1a);
  min-height: 100vh;
`;

const navStyles = css`
  background: #fff;
  border-bottom: 1px solid #e8e4df;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const navLogoStyles = css`
  font-family: 'Georgia', serif;
  font-size: 17px;
  font-weight: 400;
  color: #1a1a1a;
  padding: 16px 0;
`;

const navRightStyles = css`
  font-size: 12px;
  color: #9a9087;
`;

const pageHeaderStyles = css`
  padding: 36px 32px 0;
`;

const eyebrowStyles = css`
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #9a9087;
  margin-bottom: 10px;
`;

const pageTitleStyles = css`
  font-family: 'Georgia', serif;
  font-size: 28px;
  font-weight: 400;
  color: #1a1a1a;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
`;

const pageSubStyles = css`
  font-size: 13px;
  color: #9a9087;
  line-height: 1.6;
  max-width: 520px;
  margin-bottom: 28px;
`;

const tabsStyles = css`
  display: flex;
  border-bottom: 1px solid #e8e4df;
  padding: 0 32px;
  margin-bottom: 32px;
`;

const tabBase = css`
  font-size: 13px;
  padding: 10px 18px 14px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  color: #9a9087;
  margin-bottom: -1px;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
  &:hover {
    color: #1a1a1a;
  }
`;

const tabActiveStyles = css`
  color: #1a1a1a;
  border-bottom-color: #1a1a1a;
`;

const summaryStyles = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 0 32px;
  margin-bottom: 28px;
`;

const sumCardStyles = css`
  background: #fff;
  border: 1px solid #e0dbd4;
  border-radius: 10px;
  padding: 14px 16px;
  text-align: center;
`;

const sumValStyles = css`
  font-family: 'Georgia', serif;
  font-size: 24px;
  font-weight: 400;
  color: #1a1a1a;
  margin-bottom: 3px;
`;

const sumLblStyles = css`
  font-size: 11px;
  color: #9a9087;
  letter-spacing: 0.03em;
`;

const progressSectionStyles = css`
  padding: 0 32px;
  margin-bottom: 28px;
`;

const progressTopStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
`;

const progressLabelStyles = css`
  font-size: 12px;
  color: #1a1a1a;
  font-weight: 500;
`;

const progressCountStyles = css`
  font-size: 12px;
  color: #9a9087;
`;

const progressTrackStyles = css`
  height: 5px;
  background: #e8e4df;
  border-radius: 3px;
  overflow: hidden;
`;

const reqListStyles = css`
  padding: 0 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const reqCardBase = css`
  background: #fff;
  border: 1px solid #e0dbd4;
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: border-color 0.15s;
  &:hover {
    border-color: #c5bfb8;
  }
`;

const reqCardDone = css`
  border-left: 3px solid #6b9e5e;
`;

const reqCardPending = css`
  border-left: 3px solid #e8a838;
`;

const reqCardNotRequired = css`
  border-left: 3px solid #e0dbd4;
  opacity: 0.7;
`;

const reqIconBase = css`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

const reqContentStyles = css`
  flex: 1;
  min-width: 0;
`;

const reqTitleStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 4px;
`;

const reqDescStyles = css`
  font-size: 12px;
  color: #9a9087;
  line-height: 1.5;
  margin-bottom: 10px;
`;

const reqFooterStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const pillBase = css`
  font-size: 10px;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.02em;
`;

const ctaBase = css`
  font-size: 12px;
  padding: 7px 18px;
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.15s;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ctaGhost = css`
  background: none;
  border: 1.5px solid #e0dbd4;
  color: #1a1a1a;
  &:hover:not(:disabled) {
    border-color: #1a1a1a;
  }
`;

const ctaPrimary = css`
  background: #1a1a1a;
  border: 1.5px solid #1a1a1a;
  color: #faf9f7;
  &:hover:not(:disabled) {
    background: #333;
  }
`;

const restWrapStyles = css`
  padding: 0 32px;
`;

const restrictionCardBase = css`
  background: #fff;
  border: 1px solid #e0dbd4;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 10px;
  border-left: 3px solid #e8a838;
`;

const restrictionCardSevere = css`
  border-left-color: #c0392b;
`;

const restCardHeaderStyles = css`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`;

const restCountdownStyles = css`
  font-size: 11px;
  color: #9a9087;
  background: #f3f0eb;
  border: 1px solid #e0dbd4;
  border-radius: 20px;
  padding: 3px 10px;
  letter-spacing: 0.01em;
  span {
    font-weight: 600;
    color: #1a1a1a;
  }
`;

const restItemsStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const restItemBase = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e0dbd4;
  border-radius: 8px;
  padding: 9px 13px;
`;

const restItemAllowed = css`
  background: #f4fbf2;
  border-color: #c5dfc0;
`;

const restItemBlocked = css`
  background: #fdf4f4;
  border-color: #f0c8c8;
`;

const restItemWarn = css`
  background: #fffaf2;
  border-color: #f0dbb0;
`;

const restItemLeftStyles = css`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const restItemLabelStyles = css`
  font-size: 12px;
  color: #1a1a1a;
`;

const restItemValBase = css`
  font-size: 13px;
  font-weight: 500;
  font-family: 'Georgia', serif;
`;

const triggeredRuleStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const triggeredRuleLabelStyles = css`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9a9087;
  white-space: nowrap;
`;

const triggeredRuleNameStyles = css`
  font-size: 11px;
  color: #1a1a1a;
  background: #f3f0eb;
  border: 1px solid #e0dbd4;
  border-radius: 20px;
  padding: 2px 9px;
  font-weight: 500;
`;

const resolveHintBase = css`
  font-size: 13px;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 14px;
  line-height: 1.6;
  font-weight: 500;
`;

const resolveHintDomain = css`
  background: #fff8ec;
  color: #7a4800;
  border: 2px solid #e8a838;
  button {
    background: none;
    border: none;
    color: #7a4800;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
    margin-left: 6px;
    font-family: inherit;
    &:hover {
      color: #4a2c00;
    }
  }
`;

const resolveHintWait = css`
  background: #e8f2e5;
  color: #2e5c27;
  border: 2px solid #6b9e5e;
`;

const restCountBadge = css`
  font-size: 10px;
  font-weight: 600;
  background: #fdecea;
  color: #a33;
  border-radius: 20px;
  padding: 2px 7px;
  margin-left: 4px;
`;

const dotStyles = css`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCardStatusStyle(status: ReqStatus) {
  if (status === 'done') return reqCardDone;
  if (status === 'pending') return reqCardPending;
  return reqCardNotRequired;
}

function getValColor(valClass: string): string {
  if (valClass === 'red') return '#a33';
  if (valClass === 'amber') return '#a06000';
  return '#4a7a3e';
}

function getStatItemStyle(itemClass: string) {
  if (itemClass === 'blocked') return restItemBlocked;
  return restItemWarn;
}

function getStatDotColor(itemClass: string): string {
  if (itemClass === 'blocked') return '#c0392b';
  return '#e8a838';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <div
      css={css`
        width: 14px;
        height: 10px;
        border-left: 2px solid #6b9e5e;
        border-bottom: 2px solid #6b9e5e;
        transform: rotate(-45deg) translate(1px, -1px);
      `}
    />
  );
}

function ClockIcon() {
  return (
    <div
      css={css`
        width: 14px;
        height: 14px;
        border: 2px solid #e8a838;
        border-radius: 50%;
        position: relative;
        &::after {
          content: '';
          position: absolute;
          top: 1px;
          left: 5px;
          width: 2px;
          height: 5px;
          background: #e8a838;
        }
      `}
    />
  );
}

function MinusIcon() {
  return (
    <div
      css={css`
        width: 10px;
        height: 2px;
        background: #9a9087;
        border-radius: 1px;
      `}
    />
  );
}

function ReqIcon({ status }: { status: ReqStatus }) {
  let bgColor = '#f3f0eb';
  if (status === 'done') bgColor = '#e8f2e5';
  if (status === 'pending') bgColor = '#fff8ec';
  return (
    <div
      css={[
        reqIconBase,
        css`
          background: ${bgColor};
        `,
      ]}
    >
      {status === 'done' && <CheckIcon />}
      {status === 'pending' && <ClockIcon />}
      {status === 'not-required' && <MinusIcon />}
    </div>
  );
}

function ReqPill({ status }: { status: ReqStatus }) {
  if (status === 'done')
    return (
      <span
        css={[
          pillBase,
          css`
            background: #e8f2e5;
            color: #4a7a3e;
          `,
        ]}
      >
        Done
      </span>
    );
  if (status === 'pending')
    return (
      <span
        css={[
          pillBase,
          css`
            background: #fff8ec;
            color: #a06000;
          `,
        ]}
      >
        Pending
      </span>
    );
  return (
    <span
      css={[
        pillBase,
        css`
          background: #f3f0eb;
          color: #9a9087;
        `,
      ]}
    >
      Not required
    </span>
  );
}

function ReqCard({ req }: { req: Requirement }) {
  const [redirecting, setRedirecting] = useState(false);

  const cardStatus = getCardStatusStyle(req.status);
  const ctaStyle = req.status === 'done' ? ctaGhost : ctaPrimary;

  function handleCta() {
    if (redirecting) return;
    setRedirecting(true);
    setTimeout(() => setRedirecting(false), 1800);
  }

  return (
    <div css={[reqCardBase, cardStatus]}>
      <ReqIcon status={req.status} />
      <div css={reqContentStyles}>
        <div css={reqTitleStyles}>{req.title}</div>
        <div css={reqDescStyles}>{req.desc}</div>
        <div css={reqFooterStyles}>
          <ReqPill status={req.status} />
          {req.cta && req.status !== 'not-required' && (
            <button
              type="button"
              css={[ctaBase, ctaStyle]}
              onClick={handleCta}
              disabled={redirecting}
            >
              {redirecting ? 'Redirecting...' : req.cta}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Countdown({ countdownKey }: { countdownKey: string }) {
  const [secs, setSecs] = useState(() => getSecondsLeft(countdownKey));

  useEffect(() => {
    const id = setInterval(() => setSecs(getSecondsLeft(countdownKey)), 1000);
    return () => clearInterval(id);
  }, [countdownKey]);

  return <span css={restCountdownStyles}>{formatCountdown(secs)}</span>;
}

function ResolveHint({
  resolveType,
  onGoToSetup,
}: {
  resolveType: 'wait' | 'domain';
  onGoToSetup: () => void;
}) {
  if (resolveType === 'domain') {
    return (
      <div css={[resolveHintBase, resolveHintDomain]}>
        Verify your domain to lift this restriction.
        <button type="button" onClick={onGoToSetup}>
          Go to setup &rarr;
        </button>
      </div>
    );
  }
  return (
    <div css={[resolveHintBase, resolveHintWait]}>
      No action needed &mdash; this restriction lifts automatically after 30 days.
    </div>
  );
}

function SegmentItem({ item }: { item: LimitItem & { type: 'segment' } }) {
  return (
    <div css={[restItemBase, restItemAllowed]}>
      <div css={restItemLeftStyles}>
        <span
          css={[
            dotStyles,
            css`
              background: #6b9e5e;
            `,
          ]}
        />
        <span css={restItemLabelStyles}>{item.label}</span>
      </div>
    </div>
  );
}

function StatItem({ item }: { item: LimitItem & { type: 'stat' } }) {
  const itemStyle = getStatItemStyle(item.itemClass);
  const dotColor = getStatDotColor(item.itemClass);
  const valColor = getValColor(item.valClass);
  return (
    <div css={[restItemBase, itemStyle]}>
      <div css={restItemLeftStyles}>
        <span
          css={[
            dotStyles,
            css`
              background: ${dotColor};
            `,
          ]}
        />
        <span css={restItemLabelStyles}>{item.label}</span>
      </div>
      <span
        css={[
          restItemValBase,
          css`
            color: ${valColor};
          `,
        ]}
      >
        {item.value}
      </span>
    </div>
  );
}

function LimitItems({
  items,
  ruleName,
  onGoToSetup,
}: {
  items: LimitItem[];
  ruleName: string;
  onGoToSetup: () => void;
}) {
  if (!items.length) return null;
  return (
    <div css={restItemsStyles}>
      {items.map(item => (
        <React.Fragment key={item.label}>
          <div css={triggeredRuleStyles}>
            <span css={triggeredRuleLabelStyles}>Triggered rule</span>
            <span css={triggeredRuleNameStyles}>{ruleName}</span>
          </div>
          {item.type === 'segment' ? <SegmentItem item={item} /> : <StatItem item={item} />}
          <ResolveHint resolveType={item.resolveType} onGoToSetup={onGoToSetup} />
        </React.Fragment>
      ))}
    </div>
  );
}

function RestrictionCard({
  restriction,
  onGoToSetup,
}: {
  restriction: Restriction;
  onGoToSetup: () => void;
}) {
  const resolveType = restriction.limitItems[0]?.resolveType ?? 'wait';
  const cardStyle = restriction.severity === 'high' ? restrictionCardSevere : undefined;
  return (
    <div css={[restrictionCardBase, cardStyle]}>
      {resolveType === 'wait' && (
        <div css={restCardHeaderStyles}>
          <Countdown countdownKey={resolveType} />
        </div>
      )}
      <LimitItems
        items={restriction.limitItems}
        ruleName={restriction.title}
        onGoToSetup={onGoToSetup}
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DeliverabilityHub() {
  const [activeTab, setActiveTab] = useState<HubTab>('setup');
  const domainCardRef = useRef<HTMLDivElement | null>(null);

  const done = REQUIREMENTS.filter(r => r.status === 'done').length;
  const countable = REQUIREMENTS.filter(r => r.status !== 'not-required').length;
  const pending = REQUIREMENTS.filter(r => r.status === 'pending').length;
  const notRequired = REQUIREMENTS.filter(r => r.status === 'not-required').length;
  const pct = Math.round((done / countable) * 100);

  const activeRestrictions = RESTRICTIONS.filter(r => r.status === 'active');
  const seen = new Set<string>();
  const dedupedRestrictions = activeRestrictions.filter(r => {
    const key = r.limitItems[0]?.resolveType ?? '';
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const handleGoToSetup = useCallback(() => {
    setActiveTab('setup');
    setTimeout(() => {
      if (domainCardRef.current) {
        domainCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        domainCardRef.current.style.transition = 'box-shadow .3s';
        domainCardRef.current.style.boxShadow = '0 0 0 2px #1a1a1a';
        setTimeout(() => {
          if (domainCardRef.current) domainCardRef.current.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  }, []);

  function handleSetupTab() {
    setActiveTab('setup');
  }

  function handleRestrictionsTab() {
    setActiveTab('restrictions');
  }

  return (
    <div css={appStyles}>
      {/* Nav */}
      <div css={navStyles}>
        <div css={navLogoStyles}>flodesk</div>
        <div css={navRightStyles}>Hi, Sarah &darr;</div>
      </div>

      {/* Page header */}
      <div css={pageHeaderStyles}>
        <div css={eyebrowStyles}>Account health</div>
        <h1 css={pageTitleStyles}>Deliverability hub</h1>
        <p css={pageSubStyles}>
          {
            'Here\u2019s everything you need to set up your account for great inbox placement. Complete each step to make sure your emails land where they should.'
          }
        </p>
      </div>

      {/* Tabs */}
      <div css={tabsStyles}>
        <button
          type="button"
          css={[tabBase, activeTab === 'setup' && tabActiveStyles]}
          onClick={handleSetupTab}
        >
          Sender setup requirements
        </button>
        <button
          type="button"
          css={[tabBase, activeTab === 'restrictions' && tabActiveStyles]}
          onClick={handleRestrictionsTab}
        >
          Current restrictions <span css={restCountBadge}>{dedupedRestrictions.length}</span>
        </button>
        <button type="button" css={tabBase}>
          Sending reputation
        </button>
        <button type="button" css={tabBase}>
          Guides &amp; resources
        </button>
      </div>

      {/* Setup tab */}
      {activeTab === 'setup' && (
        <>
          <div css={summaryStyles}>
            <div css={sumCardStyles}>
              <div
                css={[
                  sumValStyles,
                  css`
                    color: #4a7a3e;
                  `,
                ]}
              >
                {done}
              </div>
              <div css={sumLblStyles}>Completed</div>
            </div>
            <div css={sumCardStyles}>
              <div
                css={[
                  sumValStyles,
                  css`
                    color: #a06000;
                  `,
                ]}
              >
                {pending}
              </div>
              <div css={sumLblStyles}>Pending</div>
            </div>
            <div css={sumCardStyles}>
              <div
                css={[
                  sumValStyles,
                  css`
                    color: #9a9087;
                  `,
                ]}
              >
                {notRequired}
              </div>
              <div css={sumLblStyles}>Not required</div>
            </div>
          </div>

          <div css={progressSectionStyles}>
            <div css={progressTopStyles}>
              <span css={progressLabelStyles}>Setup progress</span>
              <span css={progressCountStyles}>
                {done} of {countable} completed
              </span>
            </div>
            <div css={progressTrackStyles}>
              <div
                css={css`
                  height: 5px;
                  background: #1a1a1a;
                  border-radius: 3px;
                  width: ${pct}%;
                  transition: width 0.4s;
                `}
              />
            </div>
          </div>

          <div css={reqListStyles}>
            {REQUIREMENTS.map(req => (
              <div key={req.id} ref={req.id === 'domain' ? domainCardRef : undefined}>
                <ReqCard req={req} />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Restrictions tab */}
      {activeTab === 'restrictions' && (
        <div css={restWrapStyles}>
          {dedupedRestrictions.map(r => (
            <RestrictionCard key={r.id} restriction={r} onGoToSetup={handleGoToSetup} />
          ))}
        </div>
      )}
    </div>
  );
}
